import { User } from "@prisma/client";
import { Application } from "@prisma/client";
import Button, {
  ButtonType,
} from "immigram-ui-cmon/src/components/Button/Button";
import clsxm from "immigram-ui-cmon/src/lib/clsxm";
import { useTranslation } from "next-i18next";
import { useCallback, useEffect, useState } from "react";

import { isAdmin } from "@/lib/auth/rbac";
import { ExperienceType } from "@/lib/experienceType";
import useBooleanState from "@/lib/hook/useBooleanState";
import useTransparentState from "@/lib/hook/useTransparentState";

import { ImmigramFormConnected } from "@/components/ImmigramForm/ImmigramFormConnected";
import FormModal from "@/components/Modal/FormModal";
import { getExperienceIconByType } from "@/components/ProfileExperience/ExperienceCard";
import {
  COMPANY_EXP_TYPE_TO_NAMESPACE,
  ProfJourneyBestPracticeBanner,
} from "@/containers/ProfJourney";

interface ProfJourneyCompanyModalProps {
  application: Application;
  ownUser: User;
  experienceType?: ExperienceType;
  formInstanceId?: string;
  open?: boolean;
  editable?: boolean;
  isValid?: boolean;
  showErrors?: boolean;
  onClose: () => void;
  onSave: () => void;
  onFieldChange?: (itemConfig: any, value: string) => void;
  onExperienceCreate?: (formInstanceId: string) => void;
}

export function ProfJourneyCompanyModal({
  open,
  onClose,
  onSave,
  onFieldChange,
  onExperienceCreate,
  showErrors,
  editable,
  application,
  formInstanceId,
  ownUser,
  experienceType,
}: ProfJourneyCompanyModalProps) {
  const { t } = useTranslation();
  const formIsLoadingState = useBooleanState(false);

  const isO1Visa = application.visaTypeId === 2;

  const formNamespace =
    experienceType && COMPANY_EXP_TYPE_TO_NAMESPACE[experienceType];

  const [isFormValid, setIsFormValid] = useState<boolean>();
  const [showFormErrors, setShowFormErrors] = useTransparentState<
    boolean | undefined
  >(showErrors);

  useEffect(() => {
    setShowFormErrors(false);
  }, [open, setShowFormErrors]);

  const handleSaveAndCloseClick = useCallback(() => {
    if (isFormValid) {
      onSave();
    } else {
      setShowFormErrors(true);
    }
  }, [isFormValid, onSave, setShowFormErrors]);

  return (
    <FormModal
      heading={
        experienceType && (
          <div className="relative flex flex-row">
            <div className="mr-4 flex h-11 w-11 flex-row items-center justify-center rounded-full bg-black-50 lg:absolute lg:-left-14 lg:mr-0">
              {getExperienceIconByType(experienceType)}
            </div>
            {experienceType}
          </div>
        )
      }
      isSelfService={false}
      open={open || false}
      fullScreen={true}
      innerClassName="gap-y-0"
      isAdmin={isAdmin(ownUser)}
      onCollapse={onClose}
      hasCompletedButton={true}
      scrollY={!formIsLoadingState.value}
      actionButton={
        formIsLoadingState.value ? (
          <></>
        ) : (
          <Button
            className="inline-flex"
            type={ButtonType.Primary}
            disabled={showFormErrors && !isFormValid}
            onClick={handleSaveAndCloseClick}
          >
            {t(
              "application:profJourney.profJourneyCompanyModal.button.saveAndClose"
            )}
          </Button>
        )
      }
    >
      <div
        className={clsxm(
          "w-full flex-grow items-start pb-[70px] sm:grid sm:grid-cols-6 lg:mx-auto lg:grid-cols-12 lg:gap-8"
        )}
      >
        <div className="col-span-7">
          <ImmigramFormConnected
            showValidationErrors={showFormErrors}
            onIsValidChange={setIsFormValid}
            applicationId={application.id}
            formInstanceId={formInstanceId}
            formNamespace={formNamespace}
            onFormInstanceCreate={onExperienceCreate}
            onFieldChange={onFieldChange}
            onLoadingStart={formIsLoadingState.setTrue}
            onLoadingEnd={formIsLoadingState.setFalse}
            editable={editable}
            hiddenFieldNames={
              isO1Visa ? ["briefDescription", "howJoined"] : undefined
            }
            isO1Visa={isO1Visa}
          />
        </div>
        <div className="col-span-1 block"></div>
        {!isO1Visa && (
          <ProfJourneyBestPracticeBanner className="sticky top-0 lg:col-span-3 xl:col-span-4" />
        )}
      </div>
    </FormModal>
  );
}
